import { z } from 'zod';
/**
 * Documents (RELAY-943). A document belongs to a record (`entityType` + `entityId`) under a
 * `kind`; at most one document per (record, kind) is the CURRENT one — the resume a recruiter
 * sees, the others are history. Uploads are two steps: `:init` creates the row as `pending`
 * and signs an upload URL; `:complete` checks the bytes landed and flips it to `ready`.
 */
export const DOCUMENT_KINDS = ['resume', 'other'];
export const documentKindSchema = z.enum(DOCUMENT_KINDS);
export const DOCUMENT_STATUSES = ['pending', 'ready'];
export const documentStatusSchema = z.enum(DOCUMENT_STATUSES);
/** Hex SHA-256 of the uploaded bytes, computed by the uploader and stored with the row. */
export const sha256Schema = z
    .string()
    .regex(/^[0-9a-f]{64}$/, 'sha256 must be 64 lowercase hex characters');
/** What the uploader must send to register a document before uploading it. */
export const documentInitSchema = z.object({
    filename: z.string().trim().min(1).max(255),
    contentType: z.string().min(1).max(255),
    sizeBytes: z
        .number()
        .int()
        .positive()
        .max(50 * 1024 * 1024),
    kind: documentKindSchema.default('resume'),
});
export const documentCompleteSchema = z.object({
    documentId: z.uuid(),
    sha256: sha256Schema,
});
/**
 * Text extraction state of a document (RELAY-943 part 2). `none` = not requested (only a
 * current resume is extracted); `unsupported` = a type the worker cannot read (an image
 * scan, a spreadsheet) — not an error, just not searchable.
 */
export const DOCUMENT_TEXT_STATUSES = [
    'none',
    'pending',
    'processing',
    'done',
    'unsupported',
    'failed',
];
export const documentTextStatusSchema = z.enum(DOCUMENT_TEXT_STATUSES);
/**
 * A candidate's `resumeFileLink` the API will fetch for the in-app viewer (TS-1896). The link is
 * RecruitCRM's (`https://api.recruitcrm.io/v1/candidates/<slug>/resume/<token>`): it needs no
 * token, but it redirects to RecruitCRM's own S3 bucket, which sends no CORS headers, so the
 * browser cannot read it and the API passes the bytes through. Any other link (a Drive URL)
 * keeps opening in a new tab. The link itself grants access to the file: never log it.
 */
export const RESUME_LINK_HOSTS = ['api.recruitcrm.io'];
export function isViewableResumeLink(link) {
    if (!link || !URL.canParse(link))
        return false;
    const url = new URL(link);
    return (url.protocol === 'https:' && RESUME_LINK_HOSTS.includes(url.hostname));
}
//# sourceMappingURL=documents.js.map